import React, { useMemo, useState } from 'react';
import {
  Avatar, Badge, Button, Card, CardHeader, EmptyState, Table, Tabs, Tag, TextInput,
} from '@opendate-app/setlist';
import sortRows from '../booking/sortRows';
import {
  COMPETITOR_COLOR, RELATIONSHIPS, YOURS_COLOR, blendColor, competitorShare, formatDate, initials, matchesSelection,
} from './competitiveUtils';

const PAGE_SIZE = 15;
const TABS = [
  { value: 'events', label: 'Events' },
  { value: 'agencies', label: 'Agencies' },
  { value: 'agents', label: 'Agents' },
];

function historyBadge(count) {
  if (!count) return <Badge size="sm" color="error">None</Badge>;
  const label = `${count} ${count === 1 ? 'show' : 'shows'}`;
  return <Badge size="sm" color={count < 3 ? 'warning' : 'success'}>{label}</Badge>;
}

function eventColumns({ yourName, competitorName, yourCountsByAgent, onPick }) {
  const today = new Date().toISOString().slice(0, 10);
  const link = (label, level, key) => (key
    ? <Button variant="link-gray" size="sm" onClick={() => onPick(level, key)}>{label}</Button>
    : <span style={{ color: 'var(--sl-text-quaternary)' }}>—</span>);
  return [
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (row) => (
        <span className="text-nowrap">
          {formatDate(row.date, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
          {row.date > today && <span className="ml-2"><Badge size="sm" color="brand">Upcoming</Badge></span>}
        </span>
      ),
    },
    { key: 'artist', header: 'Artist', primary: true, sortable: true, render: (row) => <span style={{ fontWeight: 600 }}>{row.artist}</span> },
    {
      key: 'venue',
      header: 'Venue',
      render: (row) => (
        <span className="d-inline-flex align-items-center text-nowrap" style={{ gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: row.venue === 'competitor' ? COMPETITOR_COLOR : YOURS_COLOR }} />
          {row.venue === 'competitor' ? competitorName : yourName}
        </span>
      ),
    },
    { key: 'agency', header: 'Agency', sortable: true, render: (row) => link(row.agency, 'agency', row.agency) },
    { key: 'agent', header: 'Agent', sortable: true, render: (row) => link(row.agent, 'agent', row.agent_key) },
    {
      key: 'agent_history',
      header: `Agent's shows with ${yourName}`,
      render: (row) => (row.agent_key ? historyBadge(yourCountsByAgent[row.agent_key]) : <span style={{ color: 'var(--sl-text-quaternary)' }}>—</span>),
    },
  ];
}

function rollupColumns({ level, yourName, competitorName, onPick }) {
  return [
    {
      key: 'name',
      header: level === 'agency' ? 'Agency' : 'Agent',
      primary: true,
      sortable: true,
      render: (row) => (
        <button type="button" className="d-flex align-items-center text-left p-0 border-0 bg-transparent" style={{ gap: 12 }} onClick={() => onPick(level, row.key)}>
          <span style={{ borderRadius: '50%', background: blendColor(row) }}><Avatar size="sm" initials={initials(row.name)} /></span>
          <span>
            <span className="d-block" style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{row.name}</span>
            <span className="d-block" style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>
              {level === 'agency' ? `${row.agent_count} ${row.agent_count === 1 ? 'agent' : 'agents'}` : (row.agency || 'Unknown agency')}
            </span>
          </span>
        </button>
      ),
    },
    { key: 'yours', header: `Shows at ${yourName}`, align: 'right', sortable: true },
    { key: 'competitor', header: `Shows at ${competitorName}`, align: 'right', sortable: true },
    {
      key: 'share',
      header: 'Share with you',
      sortable: true,
      render: (row) => {
        const total = row.yours + row.competitor || 1;
        return (
          <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 160 }}>
            <div className="d-flex" style={{ flex: 1, maxWidth: 140, height: 8, borderRadius: 9999, overflow: 'hidden', background: 'var(--sl-bg-tertiary)' }}>
              <div style={{ width: `${(row.yours / total) * 100}%`, background: YOURS_COLOR }} />
              <div style={{ width: `${(row.competitor / total) * 100}%`, background: COMPETITOR_COLOR }} />
            </div>
            <span style={{ fontSize: 12, color: 'var(--sl-text-tertiary)', width: 36 }}>{`${Math.round((row.yours / total) * 100)}%`}</span>
          </div>
        );
      },
    },
    { key: 'last_competitor', header: `Last at ${competitorName}`, sortable: true, render: (row) => formatDate(row.last_competitor) },
    {
      key: 'relationship',
      header: 'Relationship',
      render: (row) => <Badge size="sm" color={RELATIONSHIPS[row.relationship].color}>{RELATIONSHIPS[row.relationship].label}</Badge>,
    },
  ];
}

const matchesSearch = (values, query) => !query || values.join(' ').toLowerCase().includes(query);

function CompetitiveDetailsTable({
  report, loading, error, selection, selectedRow, onPick, onClearSelection,
}) {
  const [tab, setTab] = useState('events');
  const [venueFilter, setVenueFilter] = useState('competitor');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [sort, setSort] = useState({ key: 'date', direction: 'desc' });

  const yourName = report?.your_venue?.name || 'Your venue';
  const competitorName = report?.competitor?.name || 'Competitor';
  const yourCountsByAgent = useMemo(
    () => Object.fromEntries((report?.agents || []).map((row) => [row.key, row.yours])),
    [report],
  );
  const query = search.trim().toLowerCase();

  const rows = useMemo(() => {
    if (!report) return [];
    if (tab === 'events') {
      return sortRows(
        report.events.filter((event) => (venueFilter === 'both' || event.venue === venueFilter)
          && matchesSelection(event, selection)
          && matchesSearch([event.artist, event.agency, event.agent], query)),
        sort,
      );
    }
    const level = tab === 'agencies' ? 'agency' : 'agent';
    const inSelection = (row) => {
      if (!selection) return true;
      if (selection.level === 'agency') return level === 'agency' ? row.key === selection.key : row.agency === selection.key;
      return level === 'agent' ? row.key === selection.key : row.key === selectedRow?.agency;
    };
    const withShare = report[tab].map((row) => ({ ...row, share: 1 - competitorShare(row) }));
    return sortRows(withShare.filter((row) => inSelection(row) && matchesSearch([row.name, row.agency], query)), sort);
  }, [report, tab, venueFilter, selection, selectedRow, query, sort]);

  const switchTab = (value) => {
    setTab(value);
    setLimit(PAGE_SIZE);
    setSort(value === 'events' ? { key: 'date', direction: 'desc' } : { key: 'competitor', direction: 'desc' });
  };
  const level = tab === 'agencies' ? 'agency' : 'agent';
  const columns = tab === 'events'
    ? eventColumns({ yourName, competitorName, yourCountsByAgent, onPick })
    : rollupColumns({ level, yourName, competitorName, onPick });
  const noun = tab === 'events' ? 'events' : tab;

  return (
    <Card
      padding="none"
      header={(
        <CardHeader
          title={(
            <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
              Details
              <Badge size="sm" color="brand">{`${rows.length} ${noun}`}</Badge>
              {selectedRow && <Tag size="sm" onDismiss={onClearSelection}>{selectedRow.name}</Tag>}
            </span>
          )}
          actions={(
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
              {tab === 'events' && (
                <Tabs
                  variant="button-border"
                  size="sm"
                  collapseBelow={0}
                  items={[{ value: 'competitor', label: competitorName }, { value: 'yours', label: yourName }, { value: 'both', label: 'Both' }]}
                  value={venueFilter}
                  onChange={(value) => { setVenueFilter(value); setLimit(PAGE_SIZE); }}
                />
              )}
              <div style={{ width: 260 }}>
                <TextInput size="sm" placeholder="Search artist, agency, agent" value={search} onChange={(event) => { setSearch(event.target.value); setLimit(PAGE_SIZE); }} />
              </div>
            </div>
          )}
        />
      )}
      footer={(
        <div className="d-flex justify-content-between align-items-center px-4 py-3" style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>
          <span>{`Showing ${Math.min(limit, rows.length)} of ${rows.length}`}</span>
          {rows.length > limit && <Button variant="secondary" size="sm" onClick={() => setLimit(limit + 25)}>Show more</Button>}
        </div>
      )}
    >
      <div className="px-4 pt-2">
        <Tabs variant="underline" size="sm" collapseBelow={0} items={TABS} value={tab} onChange={switchTab} />
      </div>
      <Table
        columns={columns}
        data={rows.slice(0, limit)}
        getRowKey={(row) => (tab === 'events' ? row.id : row.key)}
        loading={loading}
        error={error}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        empty={<EmptyState title={`No ${noun} in this range`} description="Try a wider date range or a different competitor." />}
      />
    </Card>
  );
}

export default CompetitiveDetailsTable;
